import { Injector } from '@angular/core';
import { SyncConsts } from '../AppConsts';
import { SynchronizationFrequencies } from './synchronization-frequencies';
import { SynchronizationStatusRepository } from '../local-db/repositories/synchronization-status-repository';
import { HealthCheckService } from '../services/health-check-service';
import { SynchronizationEntity } from './synchronization-entity';
import { SynchronizationState } from '../local-db/objects/synchronization-status';
import { firstValueFrom } from 'rxjs';

export abstract class SynchronizationServiceBase {
  protected readonly _entityToSync: SynchronizationEntity;

  protected abstract synchronize(): Promise<void>;

  private readonly _statusRepo: SynchronizationStatusRepository;
  private readonly _healthCheck: HealthCheckService;

  private syncInterval: number;
  private retryInterval: number;

  constructor(protected injector: Injector, entityToSync: SynchronizationEntity) {
    if (!entityToSync) {
        throw new Error('SynchronizationServiceBase requires a valid entityToSync.');
    }

    this._entityToSync = entityToSync;
    this._statusRepo = injector.get(SynchronizationStatusRepository);
    this._healthCheck = injector.get(HealthCheckService);

    this.configureFrequencies();
    setTimeout(() => this.scheduleInitialSync());
  }

  private configureFrequencies() {
    const config = SynchronizationFrequencies._frequencies[this._entityToSync];
    if (!config) {
      throw new Error(`No synchronization frequency defined for entity: ${this._entityToSync}`);
    }
    this.syncInterval = config.syncIntervalInMilliseconds();
    this.retryInterval = config.retryIntervalInMilliseconds();
  }

  private async scheduleInitialSync(): Promise<void> {
    const status = await this._statusRepo.getStatusByEntity(this._entityToSync);
    const lastSync = status?.lastSync;
    const timeout = this.getNextSyncTime(lastSync, this.syncInterval);
    await this.sleep(timeout);
    this.synchronizeAndReschedule();
  }

  private getNextSyncTime(lastSync: Date | undefined, interval: number): number {
    if (!lastSync) {
        return 0;
    }
    const diff = new Date().getTime() - lastSync.getTime();
    return diff > interval ? 0 : interval - diff;
  }

  private async synchronizeAndReschedule() {
    await this.processSynchronization();
    await this.sleep(this.syncInterval);
    this.synchronizeAndReschedule();
  }

  private async processSynchronization() {
    let done = false;
    let attempt = 0;

    while (!done && attempt < SyncConsts.MaxRetries) {
      try {
        const isOnline = await firstValueFrom(this._healthCheck.heartBeat$);
        if (!isOnline) {
            throw new Error('No connection');
        }
        await this.synchronize();
        await this._statusRepo.updateStatus(this._entityToSync, SynchronizationState.Ok);
        done = true;
      } catch (err) {
        attempt++;
        await this._statusRepo.updateStatus(this._entityToSync, SynchronizationState.Error);
        await this.sleep(this.getRetryWaitTime(attempt));
      }
    }

    if (!done) {
      console.warn(`Max retries exceeded for ${this._entityToSync}`);
    }
  }

  private getRetryWaitTime(attempts: number): number {
    return this.retryInterval * 2 ** (attempts - 1);
  }

  private sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
